import { useEffect } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry
} from "geojson";

import { useGetAnalysisSummary } from "@/api/analysis/useAnalysisAPI";
import {
  CATCHMENT_ID_ATTRIBUTE,
  GRID_ID_ATTRIBUTE,
  POLYGON_ID_ATTRIBUTE
} from "@/components/analysisSummary/filterByGrid";
import NotificationContent from "@/components/notification/notificationContent";
import { MESSAGE } from "@/constants/message";
import type { RootState } from "@/store";
import {
  setGeneralSummary,
  setGridsSelected
} from "@/store/slice/analysisSummary";
import {
  setAnalysisResultPopup,
  setShowAnalysisSummaryPopup
} from "@/store/slice/uiSlice";
import { EOutputAnalysisType } from "@/types/spatial";
import { getSourceProperties } from "@/utils/map";

import { useMap } from "../map/core/useMapContext";
import useToast, { EToastType } from "../ui/useToast";

const SUMMARY_FIT_ZOOM = 14;
const SUMMARY_OFFSET: [number, number] = [-150, 0];

export const useAnalysisResultSummary = () => {
  const dispatch = useDispatch();
  const { map, showOnlyLayer, handleMovePositionToLayer } = useMap();

  const { showToast } = useToast();

  const { analysisSummaryId, generalSummary } = useSelector(
    (state: RootState) => state.analysisSummary
  );
  const { datasets } = useSelector((state: RootState) => state.dataset);

  const { data, isSuccess, isFetching, isError } = useGetAnalysisSummary(
    analysisSummaryId || "",
    {
      enabled: !!analysisSummaryId && !generalSummary?.data
    }
  );

  const highlightAnalysisResult = () => {
    const analysisResultDataset = Object.keys(datasets)
      .map((key) => datasets[key])
      .find(
        (dataset) =>
          [
            EOutputAnalysisType.H3,
            EOutputAnalysisType.GEOHASH,
            EOutputAnalysisType.GRID,
            EOutputAnalysisType.PROFILING
          ].includes(
            dataset.data.spatial_aggregation_type as EOutputAnalysisType
          ) && dataset.data.total_rows > 0
      );
    const analysisResultDatasetId = analysisResultDataset?.data.id;

    showOnlyLayer(analysisResultDatasetId as string);

    const featureData = getSourceProperties(
      map,
      analysisResultDatasetId as string
    ) as FeatureCollection<Geometry, GeoJsonProperties>;

    const highestScoreFeature = featureData.features.reduce<
      Feature<Geometry, GeoJsonProperties>
    >(
      (acc, curr) => {
        return (curr?.properties?.["Score"] || 0) >
          (acc?.properties?.["Score"] || 0)
          ? curr
          : acc;
      },
      {} as Feature<Geometry, GeoJsonProperties>
    );

    dispatch(
      setGridsSelected([
        highestScoreFeature.properties?.[GRID_ID_ATTRIBUTE] ||
          highestScoreFeature.properties?.[CATCHMENT_ID_ATTRIBUTE] ||
          highestScoreFeature.properties?.[POLYGON_ID_ATTRIBUTE]
      ])
    );

    if (highestScoreFeature) {
      handleMovePositionToLayer(
        { type: "FeatureCollection", features: [highestScoreFeature] },
        { zoom: SUMMARY_FIT_ZOOM, offset: SUMMARY_OFFSET }
      );
    }
  };

  useEffect(() => {
    if (isFetching) {
      showToast({
        id: "generating-summary-analysis-toast",
        type: EToastType.LOADING,
        message: (
          <NotificationContent
            description={MESSAGE.ANALYSIS_SUMMARY.GENERATING_SUMMARY}
          />
        )
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  useEffect(() => {
    if (isError) {
      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            description={MESSAGE.ANALYSIS_SUMMARY.GENERYING_SUMMARY_FAILED}
          />
        )
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  useEffect(() => {
    if (data && isSuccess) {
      toast.dismiss("generating-summary-analysis-toast");
      dispatch(setShowAnalysisSummaryPopup(true));
      dispatch(setAnalysisResultPopup(false));
      dispatch(setGeneralSummary(data));
      highlightAnalysisResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data]);
};
